/**
 * Canonical permission catalogue — mirrors the backend `EUserPermissionType`
 * enum (`apps/core-service/.../user-permission-type.enum.ts`). Permission
 * names are `resource.action`. Use these constants instead of magic strings so
 * a typo is a compile error, not a silently-ungated button.
 */
export const PERMISSIONS = {
  // SRS domain modules (six actions each)
  COMPLIANCE_VIEW: 'compliance.view',
  COMPLIANCE_CREATE: 'compliance.create',
  COMPLIANCE_EDIT: 'compliance.edit',
  COMPLIANCE_DELETE: 'compliance.delete',
  COMPLIANCE_EXPORT: 'compliance.export',
  COMPLIANCE_APPROVE: 'compliance.approve',

  OPERATIONS_VIEW: 'operations.view',
  OPERATIONS_CREATE: 'operations.create',
  OPERATIONS_EDIT: 'operations.edit',
  OPERATIONS_DELETE: 'operations.delete',
  OPERATIONS_EXPORT: 'operations.export',
  OPERATIONS_APPROVE: 'operations.approve',

  // FR-TRACE-014 — custody transfer approval. SIGN is mobile-only in practice
  // (the signing key lives in the device enclave), so the web uses VIEW to show
  // the pending queue read-only and CONFIGURE for the IT-Admin policy page.
  CUSTODY_APPROVAL_VIEW: 'custody_approval.view',
  CUSTODY_APPROVAL_SIGN: 'custody_approval.sign',
  CUSTODY_APPROVAL_CONFIGURE: 'custody_approval.configure',

  WORKFORCE_VIEW: 'workforce.view',
  WORKFORCE_CREATE: 'workforce.create',
  WORKFORCE_EDIT: 'workforce.edit',
  WORKFORCE_DELETE: 'workforce.delete',
  WORKFORCE_EXPORT: 'workforce.export',
  WORKFORCE_APPROVE: 'workforce.approve',
  WORKER_IDENTITY_REFERENCE_LOOKUP: 'worker_identity_reference.lookup',
  // Uploading the payment sheet that decides who counts as verified. Held by
  // the same three roles as the lookup, so this gates a button rather than a
  // new sight of the data.
  WORKER_IDENTITY_REFERENCE_IMPORT: 'worker_identity_reference.import',
  // FR-LBR-020 — attendance capture, split out of `workforce.edit` so the
  // recorder roles (Field Supervisor, Mine Engineer, Subcontractor) can log a
  // shift without holding worker-profile edit. Attendance endpoints accept
  // either permission, so gate attendance UI on `WORKFORCE_EDIT ||
  // WORKFORCE_ATTENDANCE`.
  WORKFORCE_ATTENDANCE: 'workforce.attendance',

  SAFETY_VIEW: 'safety.view',
  SAFETY_CREATE: 'safety.create',
  SAFETY_EDIT: 'safety.edit',
  SAFETY_DELETE: 'safety.delete',
  SAFETY_EXPORT: 'safety.export',
  SAFETY_APPROVE: 'safety.approve',

  ENVIRONMENT_VIEW: 'environment.view',
  ENVIRONMENT_CREATE: 'environment.create',
  ENVIRONMENT_EDIT: 'environment.edit',
  ENVIRONMENT_DELETE: 'environment.delete',
  ENVIRONMENT_EXPORT: 'environment.export',
  ENVIRONMENT_APPROVE: 'environment.approve',

  INVENTORY_VIEW: 'inventory.view',
  INVENTORY_CREATE: 'inventory.create',
  INVENTORY_EDIT: 'inventory.edit',
  INVENTORY_DELETE: 'inventory.delete',
  INVENTORY_EXPORT: 'inventory.export',
  INVENTORY_APPROVE: 'inventory.approve',
  INVENTORY_REVERSE: 'inventory.reverse',
  INVENTORY_REQUISITION_APPROVE: 'inventory.approve_requisition',
  INVENTORY_REQUISITION_FULFILL: 'inventory.fulfill_requisition',
  INVENTORY_REQUISITION_CREATE: 'inventory.create_requisition',

  FINANCE_VIEW: 'finance.view',
  FINANCE_CREATE: 'finance.create',
  FINANCE_EDIT: 'finance.edit',
  FINANCE_DELETE: 'finance.delete',
  FINANCE_EXPORT: 'finance.export',
  FINANCE_APPROVE: 'finance.approve',
  FINANCE_PAYMENT_STATUS_MANAGE: 'finance.payment_status_manage',
  FINANCE_ACKNOWLEDGE: 'finance.acknowledge',

  PAYMENT_RUN_VIEW: 'payment_run.view',
  PAYMENT_RUN_CREATE: 'payment_run.create',
  PAYMENT_RUN_EDIT: 'payment_run.edit',
  PAYMENT_RUN_APPROVE: 'payment_run.approve',
  PAYMENT_RUN_PAY: 'payment_run.pay',

  REPORTS_VIEW: 'reports.view',
  REPORTS_CREATE: 'reports.create',
  REPORTS_EDIT: 'reports.edit',
  REPORTS_DELETE: 'reports.delete',
  REPORTS_EXPORT: 'reports.export',
  REPORTS_APPROVE: 'reports.approve',

  // Usage & adoption analytics. Distinct from `reports.*` (document
  // generation): these gate who-is-using-what, role activity and error
  // clustering. ANALYTICS_USER_DETAIL is separate because the leaderboard
  // names individual people.
  ANALYTICS_VIEW: 'analytics.view',
  ANALYTICS_EXPORT: 'analytics.export',
  ANALYTICS_USER_DETAIL: 'analytics.user_detail',

  AUDIT_VIEW: 'audit.view',
  AUDIT_CREATE: 'audit.create',
  AUDIT_EDIT: 'audit.edit',
  AUDIT_DELETE: 'audit.delete',
  AUDIT_EXPORT: 'audit.export',
  AUDIT_APPROVE: 'audit.approve',

  CONFIG_VIEW: 'config.view',
  CONFIG_CREATE: 'config.create',
  CONFIG_EDIT: 'config.edit',
  CONFIG_DELETE: 'config.delete',
  CONFIG_EXPORT: 'config.export',
  CONFIG_APPROVE: 'config.approve',

  // User management
  USER_READ: 'user.read',
  USER_WRITE: 'user.write',
  USER_DELETE: 'user.delete',
  USER_ACTIVATE: 'user.activate',
  USER_DEACTIVATE: 'user.deactivate',
  USER_SUSPEND: 'user.suspend',
  USER_BULK_ACTION: 'user.bulk_action',

  // Department management
  DEPARTMENT_READ: 'department.read',
  DEPARTMENT_WRITE: 'department.write',
  DEPARTMENT_DELETE: 'department.delete',

  // Shift management
  // ── Milestone 2 modules (Appendix B.4) ──────────────────────────────────
  // Supply Chain and Logistics (operations.logistics)
  LOGISTICS_VIEW: 'logistics.view',
  LOGISTICS_CREATE: 'logistics.create',
  LOGISTICS_EDIT: 'logistics.edit',
  LOGISTICS_DELETE: 'logistics.delete',
  LOGISTICS_EXPORT: 'logistics.export',
  LOGISTICS_APPROVE: 'logistics.approve',

  // Procurement and Vendor Management (operations.procurement)
  PROCUREMENT_VIEW: 'procurement.view',
  PROCUREMENT_CREATE: 'procurement.create',
  PROCUREMENT_EDIT: 'procurement.edit',
  PROCUREMENT_DELETE: 'procurement.delete',
  PROCUREMENT_EXPORT: 'procurement.export',
  PROCUREMENT_APPROVE: 'procurement.approve',

  // Surveying and Mapping (mining.surveying)
  SURVEYING_VIEW: 'surveying.view',
  SURVEYING_CREATE: 'surveying.create',
  SURVEYING_EDIT: 'surveying.edit',
  SURVEYING_DELETE: 'surveying.delete',
  SURVEYING_EXPORT: 'surveying.export',
  SURVEYING_APPROVE: 'surveying.approve',

  // Ore Processing Management (mining.ore_processing)
  ORE_PROCESSING_VIEW: 'ore_processing.view',
  ORE_PROCESSING_CREATE: 'ore_processing.create',
  ORE_PROCESSING_EDIT: 'ore_processing.edit',
  ORE_PROCESSING_DELETE: 'ore_processing.delete',
  ORE_PROCESSING_EXPORT: 'ore_processing.export',
  ORE_PROCESSING_APPROVE: 'ore_processing.approve',

  // Laboratory Information Management System (mining.lims)
  LIMS_VIEW: 'lims.view',
  LIMS_CREATE: 'lims.create',
  LIMS_EDIT: 'lims.edit',
  LIMS_DELETE: 'lims.delete',
  LIMS_EXPORT: 'lims.export',
  LIMS_APPROVE: 'lims.approve',

  // Geology and Resource Management (mining.geology)
  GEOLOGY_VIEW: 'geology.view',
  GEOLOGY_CREATE: 'geology.create',
  GEOLOGY_EDIT: 'geology.edit',
  GEOLOGY_DELETE: 'geology.delete',
  GEOLOGY_EXPORT: 'geology.export',
  GEOLOGY_APPROVE: 'geology.approve',

  SHIFT_VIEW: 'shift.view',
  SHIFT_WRITE: 'shift.write',
  SHIFT_DELETE: 'shift.delete',

  WORKINGPLACE_VIEW: 'workingplace.view',
  WORKINGPLACE_CREATE: 'workingplace.create',
  WORKINGPLACE_EDIT: 'workingplace.edit',
  WORKINGPLACE_DEACTIVATE: 'workingplace.deactivate',
  WORKINGPLACETYPE_EDIT: 'workingplacetype.edit',

  PLANNINGPROFILE_VIEW: 'planningprofile.view',
  PLANNINGPROFILE_EDIT: 'planningprofile.edit',
  PLANNINGMETRICS_VIEW: 'planningmetrics.view',
  PLANNINGCODES_VIEW: 'planningcodes.view',
  PLANNINGCODES_EDIT: 'planningcodes.edit',

  PLAN_VIEW: 'plan.view',
  PLAN_CREATE: 'plan.create',
  PLAN_EDIT: 'plan.edit',
  PLAN_SUBMIT: 'plan.submit',
  PLAN_APPROVE: 'plan.approve',

  SHIFTREPORT_VIEW: 'shiftreport.view',
  SHIFTREPORT_CREATE: 'shiftreport.create',
  SHIFTREPORT_EDIT: 'shiftreport.edit',
  SHIFTREPORT_SUBMIT: 'shiftreport.submit',
  SHIFTREPORT_APPROVE: 'shiftreport.approve',

  DAILYREPORT_VIEW: 'dailyreport.view',
  DAILYREPORT_EDIT: 'dailyreport.edit',
  DAILYREPORT_APPROVE: 'dailyreport.approve',
  DAILYREPORT_REOPEN: 'dailyreport.reopen',

  VARIANCE_VIEW: 'variance.view',
  VARIANCE_CONFIGURE: 'variance.configure',

  // Financial requisitions. VIEW_ALL is separate from VIEW: a holder of VIEW
  // alone only ever sees their own requisitions.
  FINANCIAL_REQUISITION_VIEW: 'financial_requisition.view',
  FINANCIAL_REQUISITION_VIEW_ALL: 'financial_requisition.view_all',
  FINANCIAL_REQUISITION_CREATE: 'financial_requisition.create',
  FINANCIAL_REQUISITION_EDIT: 'financial_requisition.edit',
  FINANCIAL_REQUISITION_CANCEL: 'financial_requisition.cancel',
  FINANCIAL_REQUISITION_APPROVE: 'financial_requisition.approve',

  // Leadership hierarchy (organisation chart / reporting lines) — not an RBAC
  // concept. WRITE covers create, edit, and assigning a post-holder.
  LEADERSHIP_HIERARCHY_READ: 'leadership_hierarchy.read',
  LEADERSHIP_HIERARCHY_WRITE: 'leadership_hierarchy.write',
  LEADERSHIP_HIERARCHY_DELETE: 'leadership_hierarchy.delete',
  LEADERSHIP_HIERARCHY_REPARENT: 'leadership_hierarchy.reparent',
  LEADERSHIP_HIERARCHY_REORDER: 'leadership_hierarchy.reorder',

  // Pending Actions (overdue work rolled up along the reporting lines). VIEW
  // shows a person their own overdue items; VIEW_SUBTREE additionally lets
  // them see the people reporting to them, directly or at any depth.
  PENDING_ACTIONS_VIEW: 'pending_actions.view',
  PENDING_ACTIONS_VIEW_SUBTREE: 'pending_actions.view_subtree',

  // Daily activity records (activity counters, sanitation rounds, waste status)
  // and the lists they pick from. VIEW also lets a person produce the daily
  // reports built from them.
  DAILYACTIVITY_VIEW: 'dailyactivity.view',
  DAILYACTIVITY_CREATE: 'dailyactivity.create',
  DAILYACTIVITY_EDIT: 'dailyactivity.edit',
  DAILYACTIVITY_DELETE: 'dailyactivity.delete',
  DAILYACTIVITY_MANAGE_LISTS: 'dailyactivity.manage_lists',

  // Payment processing
  PAYMENT_READ: 'payment.read',
  PAYMENT_PROCESS: 'payment.process',
  PAYMENT_REFUND: 'payment.refund',

  // Pricing
  PRICING_MODEL_READ: 'pricing_model.read',
  PRICING_MODEL_WRITE: 'pricing_model.write',
  PRICING_RULE_READ: 'pricing_rule.read',
  PRICING_RULE_WRITE: 'pricing_rule.write',

  // Transactions
  TRANSACTION_READ: 'transaction.read',
  TRANSACTION_EXPORT: 'transaction.export',

  // Role & permission management
  ROLE_READ: 'role.read',
  ROLE_WRITE: 'role.write',
  ROLE_DELETE: 'role.delete',
  PERMISSION_READ: 'permission.read',
  PERMISSION_WRITE: 'permission.write',
  PERMISSION_DELETE: 'permission.delete',

  // Dashboard
  DASHBOARD_READ: 'dashboard.read',
  DASHBOARD_CUSTOMIZE: 'dashboard.customize',
  DASHBOARD_CONFIGURE: 'dashboard.configure',
  KPI_TARGET_VIEW: 'kpi_target.view',
  KPI_TARGET_MANAGE: 'kpi_target.manage',

  // Settings
  SETTINGS_READ: 'settings.read',
  SETTINGS_WRITE: 'settings.write',

  // Legal documents
  LEGAL_DOCUMENT_READ: 'legal_document.read',
  LEGAL_DOCUMENT_WRITE: 'legal_document.write',
  LEGAL_DOCUMENT_PUBLISH: 'legal_document.publish',

  // Announcements
  ANNOUNCEMENT_READ: 'announcement.read',
  ANNOUNCEMENT_WRITE: 'announcement.write',
  ANNOUNCEMENT_DELETE: 'announcement.delete',
  ANNOUNCEMENT_SEND: 'announcement.send',

  // Security
  SECURITY_READ: 'security.read',
  SECURITY_WRITE: 'security.write',

  // Notifications
  NOTIFICATION_READ: 'notification.read',
  NOTIFICATION_WRITE: 'notification.write',
  NOTIFICATION_SEND: 'notification.send',

  // Incident management
  INCIDENT_READ: 'incident.read',
  INCIDENT_WRITE: 'incident.write',
  INCIDENT_DELETE: 'incident.delete',
  INCIDENT_RESOLVE: 'incident.resolve',

  // Messaging
  MESSAGING_READ: 'messaging.read',
  MESSAGING_WRITE: 'messaging.write',

  // Tenant management (platform admin)
  TENANT_VIEW: 'tenant.view',
  TENANT_CREATE: 'tenant.create',
  TENANT_EDIT: 'tenant.edit',
  TENANT_DELETE: 'tenant.delete',

  // Platform health (platform admin)
  PLATFORM_HEALTH_VIEW: 'platform_health.view',
  PLATFORM_HEALTH_EXPORT: 'platform_health.export',

  // Platform configuration (platform admin)
  PLATFORM_CONFIG_VIEW: 'platform_config.view',
  PLATFORM_CONFIG_EDIT: 'platform_config.edit',

  // Support impersonation. VIEW covers the session list and audit trail; START
  // is the act of assuming another user's identity and is deliberately separate
  // so read access can be granted without it.
  IMPERSONATION_VIEW: 'impersonation.view',
  IMPERSONATION_START: 'impersonation.start',

  // Developer & API — tenant-managed API keys, webhooks, IP allowlist.
  // DELETE is separate from REVOKE because it destroys the row (and is only
  // permitted on an already-revoked key). REPLAY is separate from MANAGE
  // because a replay re-sends a real business event.
  APIKEY_VIEW: 'apikey.view',
  APIKEY_CREATE: 'apikey.create',
  APIKEY_ROTATE: 'apikey.rotate',
  APIKEY_REVOKE: 'apikey.revoke',
  APIKEY_DELETE: 'apikey.delete',

  WEBHOOK_VIEW: 'webhook.view',
  WEBHOOK_MANAGE: 'webhook.manage',
  WEBHOOK_REPLAY: 'webhook.replay',

  IP_ALLOWLIST_VIEW: 'ip_allowlist.view',
  IP_ALLOWLIST_MANAGE: 'ip_allowlist.manage',

  // Support tickets (FR14) — CREATE/VIEW-mine are effectively everyone (every
  // role that can log in can file a ticket); VIEW/ESCALATE/RESOLVE gate the
  // tenant-wide queue for IT Administrator / Mine Director / Mine Manager.
  SUPPORT_TICKET_CREATE: 'support_ticket.create',
  SUPPORT_TICKET_VIEW: 'support_ticket.view',
  SUPPORT_TICKET_ESCALATE: 'support_ticket.escalate',
  SUPPORT_TICKET_RESOLVE: 'support_ticket.resolve',

  // Visitors Induction Form. APPROVE covers both approve and reject (a single
  // Mine Manager review decision). SIGNOUT is split from CREATE because
  // closing out a visit is a distinct act from submitting/inducting/signing
  // one in. MEDICAL gates the restricted medicalConditionDetail field only.
  VISITOR_VIEW: 'visitor.view',
  VISITOR_CREATE: 'visitor.create',
  VISITOR_APPROVE: 'visitor.approve',
  VISITOR_SIGNOUT: 'visitor.signout',
  VISITOR_REVIEW: 'visitor.review',
  VISITOR_MEDICAL: 'visitor.medical',
} as const;

export type PermissionName = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Flat list of every known permission name. Kept in lock-step with the backend
 * `EUserPermissionType` enum by `backend-service/scripts/check-permission-parity.ts`
 * (`yarn check:permission-parity`). When adding a permission, update both sides.
 */
export const ALL_PERMISSION_NAMES: readonly string[] = Object.values(PERMISSIONS);

/** Fuel issues and tank dips: the backend accepts either permission to view them. */
export const FUEL_VIEW_PERMISSIONS: string[] = [
  PERMISSIONS.SHIFTREPORT_VIEW,
  PERMISSIONS.INVENTORY_VIEW,
];

/** Wildcard returned by the backend for super admins — grants everything. */
export const ALL_PERMISSIONS = '*';

/**
 * True if `held` satisfies `required`. Super admins (`*`) pass everything.
 * Also supports resource-level wildcards in the held set (e.g. `user.*`).
 */
export function hasPermission(
  held: readonly string[] | undefined,
  required: string,
): boolean {
  if (!held || held.length === 0) return false;
  if (held.includes(ALL_PERMISSIONS)) return true;
  if (held.includes(required)) return true;
  const [resource] = required.split('.');
  return held.includes(`${resource}.*`);
}

/** True if `held` satisfies AT LEAST ONE of `required`. */
export function hasAnyPermission(
  held: readonly string[] | undefined,
  required: readonly string[],
): boolean {
  if (required.length === 0) return true;
  return required.some((p) => hasPermission(held, p));
}

/** True if `held` satisfies EVERY one of `required`. */
export function hasAllPermissions(
  held: readonly string[] | undefined,
  required: readonly string[],
): boolean {
  return required.every((p) => hasPermission(held, p));
}